import useFormatter from "@/hooks/use-formatter.hook.ts";
import { RecognitionBadge } from "@/components/recognition-badge/RecognitionBadge.tsx";
import { EmployeeRecognitionEditModal } from "@/pages/employees/employee/widgets/EmployeeRecognitionEditModal.tsx";
import { useEmployeeRecognitionRevoke } from "@/queries/employees/use-employee-recognitions.query.ts";
import { getEmployeePositionTitle } from "@/types/employees/employee-position.types.ts";
import { ApiEmployee, getEmployeeAvatarUrl } from "@/types/employees/employee.types.ts";
import { ApiEmployeeRecognition } from "@/types/recognitions/recognition.types.ts";
import {
  Button,
  ConfirmationModal,
  DropdownMenu,
  DropdownMenuItem,
  FontAwesomeIcon,
  ListItem,
  Modal,
  ModalProps,
  Stack,
  Text,
  Title,
  useModals,
} from "@skailer-com/uikit";
import { useTranslation } from "react-i18next";

export interface EmployeeRecognitionModalProps extends ModalProps {
  employee: ApiEmployee;
  recognition: ApiEmployeeRecognition;
}

export function EmployeeRecognitionModal({
  employee,
  recognition,
  ...props
}: EmployeeRecognitionModalProps) {
  const { t } = useTranslation();
  const { openModal, close } = useModals();
  const { renderEmployeeName, renderDate } = useFormatter();
  const { id } = props;

  const revoke = useEmployeeRecognitionRevoke();

  const { can_edit, can_revoke } = recognition.acl;

  const handleEdit = () => {
    openModal(EmployeeRecognitionEditModal, { employee, recognition });
    close(id);
  };

  const handleDelete = () => {
    openModal(ConfirmationModal, {
      question: t("common:attention"),
      text: t("recognition:delete_confirm", { title: recognition.recognition_title }),
      acceptTitle: t("common:delete"),
      onAccept: async () => {
        try {
          await revoke.mutateAsync({ employeeId: employee.id, itemId: recognition.id });
          close(id);
        } catch {}
      },
    });
  };

  return (
    <Modal
      {...props}
      title={t("recognition:view_title")}
      withCloser
      headerActionsSlot={
        can_edit || can_revoke ? (
          <DropdownMenu
            trigger={
              <Button
                variant={"secondary"}
                rightIcon={<FontAwesomeIcon icon={"fa-light fa-chevron-down"} />}
              >
                {t("recognition:actions")}
              </Button>
            }
          >
            {can_edit && (
              <DropdownMenuItem onClick={handleEdit} title={t("recognition:menu_edit")} />
            )}
            {can_revoke && (
              <DropdownMenuItem onClick={handleDelete} title={t("recognition:menu_delete")} />
            )}
          </DropdownMenu>
        ) : undefined
      }
      actions={
        <Button size={"lg"} className={"w-full"} onClick={() => close(id)}>
          {t("common:ok")}
        </Button>
      }
    >
      <Stack>
        <RecognitionBadge template={recognition.recognition} size={"lg"} />

        <Title
          size={"lg"}
          paddingTop={true}
          paddingBottom={true}
          header={recognition.recognition_title}
          caption={recognition.recognition.description ?? undefined}
        />

        <Stack gap={"xs"}>
          <Title size={"xs"} header={t("recognition:awarded_by")} paddingTop />
          <ListItem
            behavior={"list"}
            avatarUrl={getEmployeeAvatarUrl(recognition.awarded_by, "md")}
            title={renderEmployeeName(recognition.awarded_by)}
            caption={getEmployeePositionTitle(recognition.awarded_by) || undefined}
            value={renderDate(recognition.datetime_awarded_at)}
          />
        </Stack>

        {recognition.comment && (
          <Stack gap={"xs"}>
            <Title size={"xs"} header={t("recognition:comment")} paddingTop paddingBottom />
            <Text className={"whitespace-pre-wrap"}>{recognition.comment}</Text>
          </Stack>
        )}
      </Stack>
    </Modal>
  );
}
